import { useState } from "react"

import { toast } from "sonner"

import { CollectionTypeSelection } from "@components/selections/CollectionTypeSelection"

import { useUserSession } from "@session/UserSession"

import { Button } from "@ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@ui/dialog"
import { Input } from "@ui/input"
import { Label } from "@ui/label"
import { Textarea } from "@ui/textarea"

interface CreateCollectionModalProps {
    open: boolean
    setOpen: (open: boolean) => void
    onCreated: () => void
}
export function CreateCollectionModal({ open, setOpen, onCreated }: Readonly<CreateCollectionModalProps>) {
    const { client } = useUserSession()

    const [name, setName] = useState("")
    const [description, setDescription] = useState("")
    const [dynamic, setDynamic] = useState(false)

    const [isCreating, setIsCreating] = useState(false)

    const handleClose = () => {
        setOpen(false)
        setName("")
        setDescription("")
        setDynamic(false)
    }

    const createCollection = () => {
        setIsCreating(true)

        client.store.collections
            .create({ name, description, dynamic })
            .then(() => {
                toast.success("Collection created successfully")
                onCreated()
                handleClose()
            })
            .catch((error) => {
                toast.error(`Failed to create collection: ${error.message}`)
            })
            .finally(() => {
                setIsCreating(false)
            })
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Create Collection</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-2">
                    <div>
                        <Label>Name*</Label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Collection Name" className="flex-grow" />
                    </div>
                    <div>
                        <Label>Description</Label>
                        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Collection Description" rows={3} />
                    </div>
                    <div>
                        <Label>Type</Label>
                        <CollectionTypeSelection onChange={setDynamic} />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="destructive" onClick={() => setOpen(false)} disabled={isCreating}>
                        Cancel
                    </Button>
                    <Button variant="outline" onClick={createCollection} disabled={isCreating || !name.length}>
                        {isCreating ? "Creating..." : "Create"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
